/**
 * 描边路径（M4 补充：真实画笔描边，DESIGN.md §5.2/§6.3/§7.1；参照 Photoshop
 * "描边路径"）。
 *
 * 链路：`PathData.commands` → `flattenPath`（原样复用）→
 * `subpathToInputPoints`（`engine/path/stroke.ts`，格式转换+闭合接缝垫料）
 * → `StrokeSampler.add()/finish()`（原样复用，真正的等间距落 stamp 在这里）
 * → `StrokeBuffer.stamp()`（原样复用）→ `clipToMask`/`mergeInto`（原样复用）
 * → `store.beginPixelTransaction`/`commitPixels`（原样复用，跟
 * `brush-tool.ts#commit()` 收尾一致）。`app/heal-fill.ts` 已经证明"完全
 * 脱离真实指针事件、程序化批量调用整套画笔管线"这个模式在这个代码库里
 * 是被验证过的既有做法。
 *
 * 每个子路径各用一个独立的 `StrokeSampler`（互相没有样条上下文，多个
 * 子路径之间本来就不该平滑过渡），但共享同一个 `StrokeBuffer`——这样
 * 一次"描边路径"操作里所有子路径的重叠部分仍然遵守"flow 在同一笔画内
 * 累积、opacity 是整体上限"（§5.2），跟真实一笔画下去效果一致，不是
 * 分开的好几笔。
 *
 * `smoothing: 0`（跟交互画笔默认的 0.5 不同）：路径本身已经是精确几何，
 * 不需要手绘平滑，平滑反而会让描边偏离路径本身。`pressureSize`/
 * `pressureFlow` 都是 false：路径没有压感数据，恒定尺寸/流量。
 */
import { StrokeBuffer, type BrushColor } from '../engine/brush/buffer';
import { StrokeSampler } from '../engine/brush/sampler';
import { subpathToInputPoints } from '../engine/path/stroke';
import { flattenPath } from '../engine/path/flatten';
import { ensureActiveRasterLayer } from './raster-target';
import type { PathData } from '../document/types';
import type { LayerId } from '../engine/tile/key';
import type { AppStore } from './store';

export interface StrokePathOptions {
  readonly path: PathData;
  /** 画笔直径，文档像素。 */
  readonly size: number;
  /** 0–1。 */
  readonly hardness: number;
  /** stamp 间距，直径的倍数；缺省 0.25（Photoshop 默认）。 */
  readonly spacing?: number;
  readonly color: BrushColor;
  /** 0–1，笔画整体不透明度上限。 */
  readonly opacity: number;
  /** 目标图层解析用：文档里没有 raster 图层时，新建一层要用的 id。 */
  readonly nextLayerId: () => LayerId;
}

/**
 * 沿路径描边并合并进活动 raster 图层（没有活动 raster 图层时自动建一层）。
 * 一次调用 = 一条历史。空路径/裁到选区外全部裁没/无文档时返回 false。
 */
export function strokePathOnLayer(store: AppStore, options: StrokePathOptions): boolean {
  const document = store.document;
  if (document === null) return false;

  const subpaths = flattenPath(options.path.commands);
  if (subpaths.length === 0) return false;

  const buffer = new StrokeBuffer(document.size.width, document.size.height, options.hardness);
  let any = false;
  for (const subpath of subpaths) {
    const inputs = subpathToInputPoints(subpath);
    if (inputs.length === 0) continue;

    const sampler = new StrokeSampler({
      size: options.size,
      flow: 1,
      spacing: options.spacing ?? 0.25,
      smoothing: 0,
      pressureSize: false,
      pressureFlow: false,
    });
    for (const input of inputs) {
      for (const stamp of sampler.add(input)) {
        buffer.stamp(stamp);
        any = true;
      }
    }
    for (const stamp of sampler.finish()) {
      buffer.stamp(stamp);
      any = true;
    }
  }
  if (!any) return false;

  const selection = store.selection;
  if (selection !== null) buffer.clipToMask(selection);

  const layer = ensureActiveRasterLayer(store, options.nextLayerId);
  if (layer === null) return false;

  const label = '描边路径';
  const transaction = store.beginPixelTransaction(label);
  buffer.mergeInto(store.tiles, layer, options.color, options.opacity, transaction, 'paint');
  store.commitPixels(label, transaction.end());
  store.invalidatePixels([...buffer.tileCoords()]);
  return true;
}
